/**
 * Trivia Game - Main Container Component
 *
 * Manages the complete game lifecycle: mode selection, game play,
 * AI opponent scheduling, score tracking, and game over display.
 * Wrapped in ErrorBoundary at the integration level (GameApp.tsx).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import UniversalGameModeSelector, {
  GameModeConfig,
  GameModeSelection,
} from '../../components/UniversalGameModeSelector';
import GameInfoPanel, { GameInfoItem } from '../../components/GameInfoPanel';
import { TriviaBoard } from './TriviaBoard';
import { TriviaEngine } from '../TriviaEngine';
import { TriviaAI } from '../TriviaAI';
import {
  TriviaGameState,
  TriviaPlayer,
  TriviaAnswer,
  TriviaGameSettings,
  TriviaDifficulty,
  ALL_CATEGORIES,
} from '../types';
import { TriviaStatisticsService } from '../services/TriviaStatisticsService';
import { MultiplayerTriviaService } from '../services/MultiplayerTriviaService';
import { TriviaQuestionLoader } from '../services/TriviaQuestionLoader';
import { GameSessionManager } from '../../services/GameSessionManager';
import { StatisticsManager } from '../../services/StatisticsManager';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import './Trivia.css';

export interface TriviaGameProps {
  onExit: () => void;
  initialRoomCode?: string;
}

type GameView = 'mode-select' | 'playing' | 'game-over';

export const TriviaGame: React.FC<TriviaGameProps> = ({ onExit, initialRoomCode }) => {
  const [view, setView] = useState<GameView>('mode-select');
  const [gameState, setGameState] = useState<TriviaGameState | null>(null);
  const [lastAnswer, setLastAnswer] = useState<TriviaAnswer | null>(null);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<TriviaGameSettings | null>(null);

  const engineRef = useRef<TriviaEngine>(new TriviaEngine());
  const aiRef = useRef<TriviaAI | null>(null);
  const aiTimerRef = useRef<NodeJS.Timeout | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);

  const { isAuthenticated, guestGameCount } = useAuthentication();
  const { addNotification } = useNotifications();

  // Load extended question bank on mount
  useEffect(() => {
    const loader = TriviaQuestionLoader.getInstance();
    loader.loadQuestions().then(questions => {
      engineRef.current.setQuestionPool(questions);
    }).catch(() => {
      // Fallback to built-in 60 questions (already set in engine)
    });
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (aiTimerRef.current) {
        clearTimeout(aiTimerRef.current);
      }
      if (gameSessionIdRef.current) {
        GameSessionManager.getInstance().endGameSession(gameSessionIdRef.current, false);
      }
    };
  }, []);

  // Handle initial room code for multiplayer deep links
  useEffect(() => {
    if (initialRoomCode) {
      // For online multiplayer via deep link, we would join the room here
      // For now, show mode selector since multiplayer requires WebSocket setup
      addNotification({
        type: NotificationType.INFO,
        title: 'Room Code Detected',
        message: `Room code ${initialRoomCode} detected. Select Online mode to join.`,
        duration: 5000,
      });
    }
  }, [initialRoomCode, addNotification]);

  // Game mode selector config
  const modeConfig: GameModeConfig = {
    gameName: 'Trivia',
    gameIcon: '🧠',
    subtitle: 'Test your knowledge across 6 categories!',
    availableModes: ['ai', 'local', 'online'],
    difficulties: [
      { id: 'easy', label: 'Easy', icon: '☀️', description: 'AI answers 40% correctly' },
      { id: 'medium', label: 'Medium', icon: '⭐', description: 'AI answers 65% correctly' },
      { id: 'hard', label: 'Hard', icon: '🏆', description: 'AI answers 85% correctly' },
    ],
  };

  const handleModeStart = useCallback((selection: GameModeSelection) => {
    const newSettings: TriviaGameSettings = {
      mode: selection.mode === 'ai'
        ? 'singleplayer'
        : selection.mode === 'local'
        ? 'local-multiplayer'
        : 'multiplayer-realtime',
      aiDifficulty: (selection.difficulty as TriviaDifficulty) || 'medium',
      categories: [...ALL_CATEGORIES],
      questionsPerRound: 10,
      timePerQuestion: 15,
    };

    setSettings(newSettings);
    startGame(newSettings);
  }, []);

  const startGame = (gameSettings: TriviaGameSettings) => {
    const engine = engineRef.current;
    engine.reset();

    const players: TriviaPlayer[] = [
      {
        id: 'player-1',
        name: 'You',
        isAI: false,
        score: 0,
        streak: 0,
        correctAnswers: 0,
        totalAnswers: 0,
        averageResponseTime: 0,
      },
    ];

    if (gameSettings.mode === 'singleplayer') {
      const difficulty = gameSettings.aiDifficulty || 'medium';
      aiRef.current = new TriviaAI(difficulty);
      players.push({
        id: 'ai-player',
        name: `AI (${difficulty})`,
        isAI: true,
        score: 0,
        streak: 0,
        correctAnswers: 0,
        totalAnswers: 0,
        averageResponseTime: 0,
      });
    } else if (gameSettings.mode === 'local-multiplayer') {
      players.push({
        id: 'player-2',
        name: 'Player 2',
        isAI: false,
        score: 0,
        streak: 0,
        correctAnswers: 0,
        totalAnswers: 0,
        averageResponseTime: 0,
      });
    }

    engine.initializeGame(gameSettings, players);
    const state = engine.startRound();
    setGameState(state);
    setLastAnswer(null);
    setView('playing');

    // Start game session tracking
    gameSessionIdRef.current = GameSessionManager.getInstance().startGameSession('trivia');

    // If AI mode, schedule AI answer
    if (gameSettings.mode === 'singleplayer' && aiRef.current) {
      scheduleAIAnswer(state);
    }
  };

  const scheduleAIAnswer = (state: TriviaGameState) => {
    if (!aiRef.current || !state.currentQuestion) return;

    const ai = aiRef.current;
    const question = state.currentQuestion;
    const responseTime = ai.getResponseTime();

    setIsAIThinking(true);

    aiTimerRef.current = setTimeout(() => {
      const answer = ai.generateAnswer(question);
      const engine = engineRef.current;
      engine.submitAnswer('ai-player', answer);
      setIsAIThinking(false);

      // Update state
      const newState = engine.getState();
      setGameState(newState);
    }, responseTime);
  };

  const handleAnswer = useCallback((selectedOption: string) => {
    const engine = engineRef.current;
    const answer = engine.submitAnswer('player-1', selectedOption);

    if (answer) {
      setLastAnswer(answer);

      if (answer.isCorrect) {
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Correct!',
          message: `+${answer.pointsEarned} points`,
          duration: 2000,
        });
      }
    }

    // Move to answer reveal
    engine.revealAnswer();
    const newState = engine.getState();
    setGameState(newState);
  }, [addNotification]);

  const handleTimeUp = useCallback(() => {
    const engine = engineRef.current;
    engine.handleTimeUp();
    const newState = engine.getState();
    setGameState(newState);
    setLastAnswer(null);

    // Cancel pending AI timer
    if (aiTimerRef.current) {
      clearTimeout(aiTimerRef.current);
      aiTimerRef.current = null;
      setIsAIThinking(false);
    }
  }, []);

  const handleNextQuestion = useCallback(() => {
    const engine = engineRef.current;

    // Cancel pending AI timer
    if (aiTimerRef.current) {
      clearTimeout(aiTimerRef.current);
      aiTimerRef.current = null;
      setIsAIThinking(false);
    }

    const newState = engine.nextQuestion();
    setGameState(newState);
    setLastAnswer(null);

    if (newState.isGameOver) {
      handleGameOver(newState);
    } else if (settings?.mode === 'singleplayer' && aiRef.current) {
      scheduleAIAnswer(newState);
    }
  }, [settings]);

  const handleGameOver = (finalState: TriviaGameState) => {
    setView('game-over');

    // End session
    if (gameSessionIdRef.current) {
      GameSessionManager.getInstance().endGameSession(gameSessionIdRef.current, true);
      gameSessionIdRef.current = null;
    }

    // Determine winner
    const engine = engineRef.current;
    const winner = engine.getWinner();
    const isDraw = engine.isDraw();

    if (isDraw) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Game Over',
        message: "It's a draw! Well played!",
        duration: 5000,
      });
    } else if (winner && !winner.isAI) {
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'You Win!',
        message: `Final score: ${winner.score} points`,
        duration: 5000,
      });
    } else if (winner) {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Game Over',
        message: `${winner.name} wins with ${winner.score} points!`,
        duration: 5000,
      });
    }
  };

  const handlePlayAgain = () => {
    if (settings) {
      startGame(settings);
    } else {
      setView('mode-select');
    }
  };

  const handleBackToModes = () => {
    setView('mode-select');
    setGameState(null);
    setLastAnswer(null);
    if (aiTimerRef.current) {
      clearTimeout(aiTimerRef.current);
      aiTimerRef.current = null;
    }
  };

  const handleCreateRoom = () => {
    addNotification({
      type: NotificationType.INFO,
      title: 'Coming Soon',
      message: 'Online multiplayer is coming soon!',
      duration: 3000,
    });
  };

  const handleJoinRoom = (code: string) => {
    addNotification({
      type: NotificationType.INFO,
      title: 'Coming Soon',
      message: 'Online multiplayer is coming soon!',
      duration: 3000,
    });
  };

  // Render based on view
  if (view === 'mode-select') {
    return (
      <div className="trivia">
        <div className="trivia__mode-select">
          <UniversalGameModeSelector
            config={modeConfig}
            onStart={handleModeStart}
            onCreateRoom={handleCreateRoom}
            onJoinRoom={handleJoinRoom}
            isLoading={isLoading}
            guestGamesRemaining={Math.max(0, 3 - guestGameCount)}
            isAuthenticated={isAuthenticated}
          />
        </div>
      </div>
    );
  }

  if (view === 'game-over' && gameState) {
    const engine = engineRef.current;
    const winner = engine.getWinner();
    const isDraw = engine.isDraw();
    const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);

    return (
      <div className="trivia">
        <div className="trivia__game-over">
          <div className="trivia__game-over-header">
            <h2 className="trivia__game-over-title">
              {isDraw ? '🤝 Draw!' : winner && !winner.isAI ? '🎉 You Win!' : '💪 Game Over!'}
            </h2>
            <p className="trivia__game-over-subtitle">
              {isDraw
                ? 'Great minds think alike!'
                : winner && !winner.isAI
                ? 'Congratulations on your victory!'
                : `${winner?.name || 'AI'} wins this round!`}
            </p>
          </div>

          <div className="trivia__leaderboard">
            <h3 className="trivia__leaderboard-title">Final Scores</h3>
            {sortedPlayers.map((player, index) => (
              <div
                key={player.id}
                className={`trivia__leaderboard-item ${
                  index === 0 ? 'trivia__leaderboard-item--first' : ''
                }`}
              >
                <span className="trivia__leaderboard-rank">
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                </span>
                <span className="trivia__leaderboard-name">
                  {player.isAI ? '🤖' : '👤'} {player.name}
                </span>
                <span className="trivia__leaderboard-score">{player.score} pts</span>
                <span className="trivia__leaderboard-accuracy">
                  {player.totalAnswers > 0
                    ? Math.round((player.correctAnswers / player.totalAnswers) * 100)
                    : 0}
                  % correct
                </span>
              </div>
            ))}
          </div>

          <div className="trivia__game-over-actions">
            <button className="trivia__btn trivia__btn--primary" onClick={handlePlayAgain}>
              Play Again
            </button>
            <button className="trivia__btn trivia__btn--secondary" onClick={handleBackToModes}>
              Change Mode
            </button>
            <button className="trivia__btn trivia__btn--outline" onClick={onExit}>
              Exit to Hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'playing' && gameState) {
    return (
      <div className="trivia">
        <div className="trivia__playing">
          <TriviaBoard
            gameState={gameState}
            currentPlayerId="player-1"
            onAnswer={handleAnswer}
            onTimeUp={handleTimeUp}
            onNextQuestion={handleNextQuestion}
            isAIThinking={isAIThinking}
            lastAnswer={lastAnswer}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default TriviaGame;
